
import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowRight, Check, X, Loader2, BrainCircuit, Lock } from 'lucide-react';
import { AIMagicResult, Quadrant } from '../types';
import { Button } from './Button';
import { FREE_AI_LIMIT } from '../constants';

// Declare chrome
declare const chrome: any;

interface MagicSortModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (tasks: AIMagicResult[]) => void;
  analyzeFunction: (text: string) => Promise<AIMagicResult[]>;
  isPro: boolean;
}

export const MagicSortModal: React.FC<MagicSortModalProps> = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  analyzeFunction,
  isPro
}) => {
  const [step, setStep] = useState<'input' | 'preview'>('input');
  const [inputText, setInputText] = useState('');
  const [results, setResults] = useState<AIMagicResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [aiUsage, setAiUsage] = useState({ date: '', count: 0 });

  // Load and Reset AI usage tracker
  useEffect(() => {
    if (!isOpen) return;

    const today = new Date().toLocaleDateString();
    
    if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
      chrome.storage.local.get(['ai_usage'], (result: any) => {
        const usage = result.ai_usage || { date: today, count: 0 };
        if (usage.date !== today) {
          usage.date = today;
          usage.count = 0;
          chrome.storage.local.set({ ai_usage: usage });
        }
        setAiUsage(usage);
      });
    } else {
      // LocalStorage Fallback
      const stored = localStorage.getItem('ai_usage');
      let usage = stored ? JSON.parse(stored) : { date: today, count: 0 };
      if (usage.date !== today) {
        usage.date = today;
        usage.count = 0;
        localStorage.setItem('ai_usage', JSON.stringify(usage));
      }
      setAiUsage(usage);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAnalyze = async () => {
    if (!inputText.trim()) return;
    
    // Check usage limit
    if (!isPro && aiUsage.count >= FREE_AI_LIMIT) {
      return; // Button is disabled, but safety check
    }

    setIsLoading(true);
    try {
      const analyzed = await analyzeFunction(inputText);
      setResults(analyzed);
      setStep('preview');

      // Update Usage Tracker
      if (!isPro) {
        const newUsage = { ...aiUsage, count: aiUsage.count + 1 };
        setAiUsage(newUsage);
        if (typeof chrome !== 'undefined' && chrome.storage && chrome.storage.local) {
          chrome.storage.local.set({ ai_usage: newUsage });
        } else {
          localStorage.setItem('ai_usage', JSON.stringify(newUsage));
        }
      }
    } catch (error) {
      alert("Failed to analyze. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = () => {
    onConfirm(results);
    handleClose();
  };

  const handleClose = () => {
    setStep('input');
    setInputText('');
    setResults([]);
    onClose();
  };

  const getBadgeColor = (q: Quadrant) => {
     switch(q) {
        case Quadrant.Do: return 'bg-red-100 text-red-700';
        case Quadrant.Schedule: return 'bg-blue-100 text-blue-700';
        case Quadrant.Delegate: return 'bg-yellow-100 text-yellow-700';
        case Quadrant.Eliminate: return 'bg-emerald-100 text-emerald-700';
     }
  };

  const creditsLeft = FREE_AI_LIMIT - aiUsage.count;
  const isLimitReached = !isPro && creditsLeft <= 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white flex justify-between items-start">
           <div>
             <h2 className="text-xl font-bold flex items-center gap-2">
               <Sparkles className="text-yellow-300" />
               AI Magic Sort
             </h2>
             <p className="text-indigo-100 text-sm mt-1">
               {step === 'input' 
                 ? "Brain dump your tasks, let AI organize them." 
                 : "Review the AI categorization."}
             </p>
           </div>
           <button onClick={handleClose} className="text-white/70 hover:text-white">
             <X size={24} />
           </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {step === 'input' ? (
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                What's on your mind?
              </label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                disabled={isLimitReached}
                placeholder={isLimitReached ? "Daily AI limit reached. Upgrade to Pro for unlimited usage." : "E.g.:\n- Finish the Q3 report by Friday\n- Buy milk\n- Call Mom for her birthday\n- Watch the new series"}
                className={`w-full h-48 p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none bg-gray-50 text-gray-800 placeholder-gray-400 ${isLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              {!isPro && (
                <div className={`flex items-center justify-between gap-2 text-xs p-2 rounded border ${isLimitReached ? 'bg-orange-50 text-orange-700 border-orange-100' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                  <div className="flex items-center gap-2">
                    <BrainCircuit size={14} />
                    <span>{isLimitReached ? "You've used all 3 AI insights for today." : `${creditsLeft} AI Sorts remaining today.`}</span>
                  </div>
                  {isLimitReached && <span className="font-bold flex items-center gap-1 uppercase tracking-tighter"><Lock size={10}/> Unlock Pro</span>}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {results.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50">
                  <div className={`mt-0.5 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider min-w-[80px] text-center ${getBadgeColor(item.quadrant)}`}>
                    {item.quadrant}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{item.title}</p>
                    <p className="text-xs text-gray-500 italic mt-0.5">{item.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
          {step === 'input' ? (
            <>
              <Button variant="ghost" onClick={handleClose}>Cancel</Button>
              <Button 
                onClick={handleAnalyze} 
                disabled={isLoading || !inputText.trim() || isLimitReached}
                className={`bg-purple-600 hover:bg-purple-700 text-white min-w-[140px] shadow-lg shadow-purple-200 ${isLimitReached ? 'grayscale opacity-50' : ''}`}
              >
                {isLoading ? (
                  <><Loader2 className="animate-spin mr-2" size={18}/> Thinking...</>
                ) : (
                  <><Sparkles className="mr-2" size={18}/> {isLimitReached ? 'No Credits' : 'Magic Sort'}</>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStep('input')}>Back to Edit</Button>
              <Button 
                onClick={handleImport} 
                className="bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-200"
              >
                <Check className="mr-2" size={18}/> Import {results.length} Tasks
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
